import { config } from '../../config';
import type { Crumb } from '../components/Layout/crumbs';
import type { SearchBarProps } from '../components/SearchBar';
import { type ButtonProps } from '@presov.sk/presov-ui-react';
import React from 'react';
import { useSearchParams } from 'react-router';

export type PageAction = {
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: any) => void;
  icon?: React.ReactNode;
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  formId?: string;
  type?: 'submit' | 'button';
  disabled?: boolean;
  loading?: boolean;
  children?: React.ReactNode;
};

type PageTab = {
  label: string | React.ReactNode;
  value: string;
  queryParamValue?: string;
};

type LayoutContextType = {
  pageTitle?: string;
  setPageTitle: (title: string | undefined) => void;
  backPath?: string;
  setBackPath: (path: string | undefined) => void;
  /**
   * Stránka je koreň sekcie - hlavička z knižnice na nej nekreslí šípku Späť.
   * Default je obrátený (šípka všade), takže označiť treba len koreň; ide
   * priamo do `page.root` shellu.
   */
  isRootPage: boolean;
  setIsRootPage: (isRoot: boolean) => void;
  actions?: PageAction[];
  setActions: React.Dispatch<React.SetStateAction<PageAction[]>>;
  tabs: PageTab[];
  setTabs: (tabs: PageTab[]) => void;
  selectedTab?: string;
  setSelectedTab: (value: string) => void;
  isLoadingTabs?: boolean;
  setIsLoadingTabs: (isLoading: boolean) => void;
  setTabQueryParamName: (name: string) => void;
  breadcrumbs: Crumb[];
  setBreadcrumbs: React.Dispatch<React.SetStateAction<Crumb[]>>;
  withoutLayoutPadding: boolean;
  setWithoutLayoutPadding: (withoutPadding: boolean) => void;
  searchBarParams?: SearchBarProps;
  setSearchBarParams: (params: SearchBarProps) => void;
  searchValue?: string;
  setSearchValue: (value: string) => void;
  footer: React.ReactNode | null;
  setFooter: (footer: React.ReactNode | null) => void;
  /**
   * Stĺpec napravo od plochy stránky - trvalý filter zoznamu. Ide do shellu,
   * nie do stránky, aby začínal na úrovni hlavičky stránky a nie pod ňou.
   * Na mobile ho shell ignoruje; tam je filter sheet, ktorý si otvára stránka.
   */
  aside: React.ReactNode | null;
  setAside: (aside: React.ReactNode | null) => void;
  /**
   * Lišta filtrov v hlavičke stránky - hľadanie a rýchle filtre zoznamu.
   *
   * Ide do hlavičky, nie na začiatok obsahu: hlavička stojí **nad** skrolovacím
   * obalom, takže filtre pri skrolovaní zoznamu neodídu. Kým si ju stránka
   * kreslila ako prvý blok obsahu, odskrolovala sa s ním.
   */
  pageFilters: React.ReactNode | null;
  setPageFilters: (filters: React.ReactNode | null) => void;
  /**
   * Tichý údaj vedľa titulu stránky - počet položiek zoznamu, stav záznamu.
   * Titul dodáva route cez `RouteMetadata`, toto dodáva stránka, lebo číslo
   * pozná až po načítaní dát.
   */
  pageMeta: React.ReactNode | null;
  setPageMeta: (meta: React.ReactNode | null) => void;
};

const LayoutContext = React.createContext<LayoutContextType | undefined>(
  undefined,
);

type Props = React.PropsWithChildren;

const LayoutProvider: React.FC<Props> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [pageTitle, setPageTitle] = React.useState<string>();
  const [backPath, setBackPath] = React.useState<string>();
  const [isRootPage, setIsRootPage] = React.useState<boolean>(false);
  const [actions, setActions] = React.useState<PageAction[]>([]);
  const [tabs, setTabs] = React.useState<PageTab[]>([]);
  const [tabQueryParamName, setTabQueryParamName] = React.useState<string>(
    config.DEFAULT_TAB_QUERY_PARAM_NAME,
  );
  const [isLoadingTabs, setIsLoadingTabs] = React.useState<boolean>(false);
  const [selectedTab, setSelectedTabInternalState] = React.useState<string>();
  const [searchBarParams, setSearchBarParams] =
    React.useState<SearchBarProps>();
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [footer, setFooter] = React.useState<React.ReactNode | null>(null);
  const [aside, setAside] = React.useState<React.ReactNode | null>(null);
  const [pageFilters, setPageFilters] = React.useState<React.ReactNode | null>(
    null,
  );
  const [pageMeta, setPageMeta] = React.useState<React.ReactNode | null>(null);

  const setSelectedTab = React.useCallback(
    (value: string, queryParamValue?: string) => {
      setSelectedTabInternalState(value);
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(tabQueryParamName, queryParamValue ?? value);
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams, tabQueryParamName],
  );

  React.useEffect(() => {
    if (!isLoadingTabs) {
      const tab = searchParams.get(tabQueryParamName);
      if (tab) {
        setSelectedTabInternalState(
          tabs.find((t) => t.value === tab || t.queryParamValue === tab)?.value,
        );
      } else if (tabs[0]?.value) {
        setSelectedTab(tabs[0]?.value);
      }
    }
  }, [searchParams, tabs, tabQueryParamName, setSelectedTab, isLoadingTabs]);

  // `href` je nepovinné: úroveň bez vlastnej stránky (napr. „Administrácia")
  // je v ceste tichý text, nie odkaz nikam.
  const [breadcrumbs, setBreadcrumbs] = React.useState<Crumb[]>([]);
  const [withoutLayoutPadding, setWithoutLayoutPadding] =
    React.useState<boolean>(false);

  return (
    <LayoutContext.Provider
      value={{
        pageTitle,
        setPageTitle,
        backPath,
        setBackPath,
        isRootPage,
        setIsRootPage,
        actions,
        setActions,
        breadcrumbs,
        setBreadcrumbs,
        withoutLayoutPadding,
        setWithoutLayoutPadding,
        tabs,
        setTabs,
        selectedTab,
        setSelectedTab,
        isLoadingTabs,
        setIsLoadingTabs,
        setTabQueryParamName,
        searchBarParams,
        setSearchBarParams,
        searchValue,
        setSearchValue,
        footer,
        setFooter,
        aside,
        setAside,
        pageFilters,
        setPageFilters,
        pageMeta,
        setPageMeta,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export default LayoutProvider;

export const useLayout = () => {
  const context = React.useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
